/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { useApiClient, useFetch, useFetchImmutable } from '@marageti/z4-lib';
import {
  CountrySetting, MatchSuggestion, PartnerMatchCategory, TripMatch, TripMatchRequest, TripRequest,
} from '@marageti/z4-sdk/lib/travel';
import { CountryCodeMap, MatchingNoteName } from '@marageti/z4-sdk/lib/standards';
import { useSnackbar } from 'notistack';
import { ProviderProps } from '../../types';
import {
  BucketedSuggestions,
  ExistingMatchSelection,
  MatchSelection,
  ValueProps,
} from './types';
import useMatchingReducer from './use-matching-reducer';
import {
  setAdditionalComments,
  setCooperativeMatch,
  setError,
  setExistingMatches,
  setSelectedCountries,
  setSelectedMatches,
  setSpecialNotes,
} from './actions';
import { excludedNotes } from '../../../utils/trip-requests';
import { formatAgeGroups } from '../../../utils/helpers';

export const MatchingStore = React.createContext({} as ValueProps);

export const MatchingProvider = ({ children }: ProviderProps) => {
  const [matchingState, matchingDispatch] = useMatchingReducer();
  const {
    additionalComments,
    cooperativeMatch,
    selectedCountries,
    selectedMatches,
    specialNotes,
  } = matchingState;

  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { tripRequestId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { data: countryCodes, isLoading: isLoadingCC } = useFetchImmutable<CountryCodeMap>(apiClient.standardsClient.getCountryCodes, [], true);
  const { data: countrySettings, isLoading: isLoadingCS } = useFetchImmutable<CountrySetting[]>(apiClient.countrySettingsClient.getCountrySettings, [], true);
  const { data: tripRequest, isLoading: isLoadingTR } = useFetch<TripRequest>(apiClient.tripRequestsClient.getTripRequest, [tripRequestId], !!tripRequestId);
  const { data: tripMatches } = useFetch<TripMatch[]>(apiClient.tripRequestsClient.getTripRequestTripMatches, [tripRequestId], !!tripRequestId);
  const { data: suggestions, isLoading: isLoadingS } = useFetch<MatchSuggestion[]>(
    apiClient.tripRequestsClient.postTripMatchSuggestions,
    [tripRequestId, { countries: selectedCountries }],
    !!(selectedCountries && selectedCountries?.length > 0),
  );

  useEffect(() => {
    // Update initial cooperative match flag if any existing matches are cooperative
    if (tripMatches) {
      const hasExistingCooperativeMatch = tripMatches.some((tripMatch) => tripMatch.cooperative);
      matchingDispatch(setCooperativeMatch(hasExistingCooperativeMatch));
    }
  }, [tripMatches]);

  useEffect(() => {
    if (tripRequest) {
      matchingDispatch(setSelectedCountries(tripRequest.countries));

      // Format age groups information
      const ageGroupsInfo = formatAgeGroups(tripRequest.adultAgeGroups, tripRequest.childAgeGroups);

      if (tripRequest.matchingNotes) {
        const notes = Object.keys(tripRequest.matchingNotes).filter((n) => !excludedNotes.includes(n as MatchingNoteName));
        matchingDispatch(setSpecialNotes(notes));

        // Get existing additional comments
        const existingComments = tripRequest.matchingNotes[MatchingNoteName.AdditionalComments]?.body || '';

        // Check if age groups info is already present
        const hasAgeGroupsInfo = existingComments.includes('Traveler Age Groups:');

        // Combine age groups info with existing comments
        const newComments = !hasAgeGroupsInfo && ageGroupsInfo
          ? existingComments
            ? `${ageGroupsInfo}${existingComments}`
            : ageGroupsInfo
          : existingComments;

        matchingDispatch(setAdditionalComments(newComments));
      } else if (ageGroupsInfo) {
      // If no existing notes but we have age groups info, set it
        matchingDispatch(setAdditionalComments(ageGroupsInfo));
      }
    }
  }, [tripRequest]);
  useEffect(() => {
    // If the trip was previously matched, pre-select the matches
    // We may filter these suggestions out later, waiting for product feedback
    if (tripMatches && tripMatches.length > 0 && suggestions) {
      const existingMatches: { [key: string]: ExistingMatchSelection } = { ...selectedMatches };
      tripMatches.forEach((tripMatch) => {
        if (!existingMatches[tripMatch.partnerId]) {
          const match = suggestions.find((suggestion) => suggestion.partner.id === tripMatch.partnerId);
          existingMatches[tripMatch.partnerId] = {
            match: match || { partner: { id: tripMatch.partnerId, name: tripMatch.partnerName } } as Partial<MatchSuggestion>,
            forCountries: tripMatch.countries,
          };
        }
      });
      matchingDispatch(setExistingMatches(existingMatches));
    }
  }, [tripMatches, suggestions]);

  const handleError = (error: ApiError) => {
    if (error?.code === 401) navigate('/logout');
    if (error?.code === 404) navigate('/404', { state: { error } });
    matchingDispatch(setError(error));
  };

  // Select match and save which countries the match applies to which is used for cooperative matches
  const onClickMatch = (match: MatchSuggestion) => {
    if (selectedMatches[match.partner.id]) {
      const { [match.partner.id]: removed, ...rest } = selectedMatches;
      matchingDispatch(setSelectedMatches(rest));
    } else {
      matchingDispatch(setSelectedMatches({ ...selectedMatches, [match.partner.id]: { match, forCountries: selectedCountries || [] } }));
    }
  };

  // Map of country code to Zicasso minimum budget per person per day
  const countryMinPPPDs = useMemo(() => {
    const mins: { [key: string]: number } = {};
    if (countrySettings) {
      countrySettings.forEach((countrySetting: CountrySetting) => {
        mins[countrySetting.isoCode2] = countrySetting.minBudgetPPPD;
      });
    }
    return mins;
  }, [countrySettings]);

  // Match suggestions are bucketed by priority
  const suggestionsList = useMemo(() => {
    const bucketedSuggestions: BucketedSuggestions = {
      [PartnerMatchCategory.NEW]: [],
      [PartnerMatchCategory.PRIORITY]: [],
      [PartnerMatchCategory.SECOND_MATCH]: [],
      [PartnerMatchCategory.THIRD_MATCH]: [],
      [PartnerMatchCategory.LAST_RESORT]: [],
    };
    if (suggestions) {
      suggestions.forEach((suggestion: MatchSuggestion) => {
        const { matchCategory } = suggestion;
        bucketedSuggestions[matchCategory].push(suggestion);
      });
    }
    return bucketedSuggestions;
  }, [suggestions]);

  const onClickConfirmMatches = () => {
    if (tripRequest) {
      setIsLoading(true);
      // Create a trip match for each selected match suggestion
      const newMatches: TripMatchRequest[] = Object.values(selectedMatches).map((matchSelection) => ({
        countries: matchSelection.forCountries,
        partnerId: matchSelection.match.partner.id,
        partnerName: matchSelection.match.partner.name,
        travelers: tripRequest.travelers,
        tripRequestId: tripRequest.id,
        cooperative: cooperativeMatch,
      }));

      const requestBody = {
        tripMatches: newMatches,
        id: tripRequest.id,
        matchingNotes: {},
      };

      if (specialNotes.length > 0 || additionalComments) {
        requestBody.matchingNotes = {
          ...specialNotes.reduce((acc, note) => ({ ...acc, [note]: { title: '', body: '' } }), {}),
          ...(additionalComments ? { [MatchingNoteName.AdditionalComments]: { title: '', body: additionalComments } } : {}),
        };
      }

      apiClient.tripRequestsClient.postTripRequestTripMatches(tripRequest.id, requestBody)
        .then(async () => {
          // Update existing trip matches with cooperative flag
          if (tripMatches) {
            await Promise.all(tripMatches.map((tripMatch) => apiClient.tripMatchesClient.putTripMatch(tripMatch.id, {
              ...tripMatch,
              cooperative: cooperativeMatch,
            })));
          }
          enqueueSnackbar('Trip matched successfully.', { variant: 'success' });
          navigate(-1);
        })
        .catch(handleError)
        .finally(() => setIsLoading(false));
    }
  };

  const value: ValueProps = useMemo(
    () => ({
      ...matchingState,
      countryCodes,
      countryMinPPPDs,
      isLoading: isLoading || isLoadingS || isLoadingCS || isLoadingTR || isLoadingCC,
      onClickConfirmMatches,
      onClickMatch,
      setAdditionalComments: (aC: string) => matchingDispatch(setAdditionalComments(aC)),
      setCooperativeMatch: (cooperative: boolean) => matchingDispatch(setCooperativeMatch(cooperative)),
      setSelectedCountries: (countries: string[] | undefined) => matchingDispatch(setSelectedCountries(countries)),
      setSelectedMatches: (partners: { [key: string]: MatchSelection }) => matchingDispatch(setSelectedMatches(partners)),
      setSpecialNotes: (sN: MatchingNoteName[]) => matchingDispatch(setSpecialNotes(sN)),
      suggestionsList,
      tripRequest,
    }),
    [
      countryCodes,
      countryMinPPPDs,
      isLoadingCC,
      isLoadingCS,
      isLoadingS,
      isLoadingTR,
      matchingState,
      onClickMatch,
      suggestionsList,
      tripRequest,
    ],
  );

  return (
    <MatchingStore.Provider value={value}>
      {children}
    </MatchingStore.Provider>
  );
};
